import {observer} from 'mobx-react-lite'
import React, {useEffect, useLayoutEffect, useState} from 'react'
import tw from 'tailwind-styled-components'
import {Lang} from 'yougile-lang'
import {logger} from 'yougile-util'
import {Hub} from 'yougile-model'
import {CompanyPreferencesClient} from '@client/company-preferences-client'
import {PermissionsChecker} from '@client/permissions-checker'
import {Board} from '@components/board'
import BoardSettings from '@components/board/board-settings/board-settings'
import {boardSettingsStore} from '@components/board/board-settings/board-settings-store'
import {BoardContext, BoardStore} from '@components/board/board-store'
import {NewDealPopup} from '@components/board/crm/new-deal-popup'
import {isCollapsedHeader} from '@components/board/general-board-settings-popup/collapsed-header-checkbox'
import {boardViewStore} from '@components/board-view'
import {BoardView} from '@components/board-view/board-view'
import {Calendar} from '@components/calendar/calendar'
import {ErrorBoundary} from '@components/common/error-boundary/error-boundary'
import {crmStore} from '@components/crm/crm-store'
import {CrmActivitiesView} from '@components/crm-activities-view/crm-activities-view'
import {useDnDStore} from '@components/draggable'
import {MoveToBody} from '@components/move-to-body'
import {CreatedAtStickerDropdown} from '@components/statistics/crm-analytics/created-at-sticker-dropdown'
import {CrmAnalyticsBase} from '@components/statistics/crm-analytics/crm-analytics-base'
import {useCrmAnalyticsStore} from '@components/statistics/crm-analytics/hooks'
import {CreateMirroredTaskPopup} from '@components/statistics/stats-reports/create-mirror-popup'
import {StatsReportsEdit} from '@components/statistics/stats-reports/stats-reports-edit'
import {StatsPage} from '@components/stats-page'
import {StickerPanel} from '@components/sticker/sticker-panel/sticker-panel'
import {Timeline} from '@components/timeline/timeline'
import {TimelineNew} from '@components/timeline-new'
import {
  TimelineNewContext,
  TimelineNewStore
} from '@components/timeline-new/timeline-new-store'
import {UiElementFor} from '@components/ui-element-for'
import {UpperPanel} from '@components/upper-panel'
import {BoardTabs} from '@components/upper-panel/board-tabs'
import {useBoard, useForceUpdate} from '@containers/hooks'
import {cn} from '@containers/html-utils'
import {globals} from '@extensions/globals-base'
import {PageService} from '@extensions/page-service'
import {ProjectContext, ProjectStore, useProjectStore} from './project-store'

const TwStickerPanelWrapper = tw.div`
  relative
  my-[8px]
  mr-[24px]
`

const TwProjTitle = tw.div`
  proj-title
  relative
  pt-12
  pl-16
  pr-12
  no-select
  z-[2000]
  bg-panel-background-default
  [backdrop-filter:blur(40px)]
`

const TwTimelineNew = tw(TimelineNew)`
  absolute
  top-[132px]
  left-16
  right-0
  bottom-0
  overflow-hidden
  rounded-tl-[12px]
`

export const Project = () => {
  const [projectStore] = useState(() => new ProjectStore())

  return (
    <ErrorBoundary
      silent
      onError={e => logger.error('project error ' + e.message)}
    >
      <ProjectContext.Provider value={projectStore}>
        <ProjectBase />
      </ProjectContext.Provider>
    </ErrorBoundary>
  )
}

const ProjectBase = observer(() => {
  const [state, store] = useProjectStore()
  const crmAnalyticsStore = useCrmAnalyticsStore()
  const [timelineStore] = useState(
    () => new TimelineNewStore(boardSettingsStore)
  )
  useEffect(timelineStore.init, [timelineStore])

  const [boardStore] = useState(() => new BoardStore())
  const boardState = boardStore.state

  useEffect(() => {
    const dispose = boardStore.scheduleColumnHeightUpdate()
    return () => {
      dispose()
      boardSettingsStore.close()
    }
  }, [])

  const board = useBoard()
  const canStickersBeShown = checkStickersToShow(board)
  const project = PageService.getCurrentProject()
  const boardView = boardViewStore.state.view

  const {useNewGantt} = CompanyPreferencesClient.get(globals.getCompanyId())

  useLayoutEffect(() => {
    store.syncOnNew()
    return () => store.dispose()
  }, [state.boardId])

  // HACK: until sticker panel is moved to BoardView
  let stickerPanelMargin = Lang.getLang() === 'en' ? 258 : 269

  if (crmStore.isCrmBoard(board)) {
    stickerPanelMargin = Lang.getLang() === 'en' ? 196 : 286
  }

  const forceUpdate = useForceUpdate()
  useEffect(() => {
    Lang.addLangChangeHandler(forceUpdate)
    return () => Lang.removeLangChangeHandler(forceUpdate)
  }, [forceUpdate])

  if (!project) {
    return null
  }

  return (
    <BoardContext.Provider value={boardStore}>
      <TimelineNewContext.Provider value={timelineStore}>
        <div className="proj-item">
          {!project && (
            <div className="proj-item-not-found">
              {Lang.msg.project404 + ' ' + PageService.getPath()}
            </div>
          )}
          {!!project && (
            <div>
              <ProjectTitle />

              {!globals.sharedId && !!board && <BoardView />}

              <div className={cn(boardView === 'board' && 'relative')}>
                {boardView === 'board' && <DnDOverlayNoPointerEvents />}

                <BoardSettings />
                {crmStore.isNewDealPopupOpened && <NewDealPopup />}

                {!globals.sharedId && (
                  <TwStickerPanelWrapper
                    id="sticker-panel-cnt"
                    style={{marginLeft: stickerPanelMargin}}
                  >
                    {boardView &&
                      ['calendar', 'board'].includes(boardView) &&
                      canStickersBeShown && (
                        <UiElementFor objId="sticker-panel" id="sticker-panel">
                          <StickerPanel
                            location={
                              boardView === 'board' ? 'board' : 'calendar'
                            }
                          />
                        </UiElementFor>
                      )}
                    {boardView === 'analytics' && (
                      <CreatedAtStickerDropdown
                        className="cursor-pointer"
                        crmAnalyticsStore={crmAnalyticsStore}
                      />
                    )}
                  </TwStickerPanelWrapper>
                )}

                {!canStickersBeShown && (
                  <div className="w-full h-32 my-[8px]">
                    <div className="ui-cnt" />
                  </div>
                )}

                {boardView === 'timeline' &&
                  !!board &&
                  (useNewGantt ? (
                    <TwTimelineNew />
                  ) : (
                    <Timeline className="mt-20" />
                  ))}
                {boardView === 'calendar' && !!board && <Calendar />}
                {state.statsOpen && (
                  <MoveToBody>
                    <div className="quick-stats-cnt">
                      <div
                        className="quick-stats-overlay"
                        onClick={() => globals.closeStats()}
                      />
                      <div className="quick-stats-wnd">
                        <div
                          className="quick-stats-close _close-big"
                          onClick={() => globals.closeStats()}
                        />
                        <StatsPage board={board} />
                      </div>
                    </div>
                  </MoveToBody>
                )}

                {boardView === 'board' && !!board && (
                  <UiElementFor objId={board.id}>
                    <div className="ui-cnt task-list-ui-cnt" />
                    <Board board={board} />
                  </UiElementFor>
                )}

                {!boardState.columnShowAdd && (
                  <StatsReportsEdit
                    initialReport={boardState.initialReport}
                    saveReport={boardStore.saveReport}
                    deleteReport={boardStore.deleteReport}
                    close={boardStore.onReportEditClose}
                    canEdit={!!boardState.canEditReport}
                  />
                )}

                {boardView === 'analytics' && (
                  <CrmAnalyticsBase crmAnalyticsStore={crmAnalyticsStore} />
                )}

                {boardView === 'activities' && <CrmActivitiesView />}
              </div>

              <CreateMirroredTaskPopup />
            </div>
          )}
        </div>
      </TimelineNewContext.Provider>
    </BoardContext.Provider>
  )
})

const ProjectTitle = () => {
  const collapsedHeader = isCollapsedHeader()

  return (
    <TwProjTitle
      style={collapsedHeader ? {paddingTop: '0', paddingLeft: '8px'} : {}}
    >
      {!collapsedHeader && <UpperPanel />}
      <BoardTabs />
    </TwProjTitle>
  )
}

const checkStickersToShow = (board: Hub): boolean =>
  board && PermissionsChecker.checkShowBoardStickers(board)

const DnDOverlayNoPointerEvents = observer(() => {
  const [, dndStore] = useDnDStore()

  if (!dndStore.getStarted('task')) {
    return null
  }

  return (
    <div className="absolute left-0 top-0 bg-transparent h-full w-full cursor-default z-2000" />
  )
})
